import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, ChevronDown, ChevronRight, FileText, Play, RotateCcw, Save, Trash2, Zap, Search, Loader2 } from 'lucide-react';
import {
  ProjectExposeCover,
  ProjectExposePage2,
  ProjectExposePage3,
  ProjectExposePage4,
} from '../sections/expose/ProjectExposePages';
import {
  DEFAULT_EXPOSE_BITRIX_MAPPING,
  EXPOSE_AUTOMATION_VARIANTS,
  EXPOSE_FIELD_DEFAULTS_CONTRACT_KEY,
  EXPOSE_PLACEHOLDER_FIELDS,
  buildExposeDataFromResolvedFields,
  buildExposePlaceholderData,
  cleanupLegacyExposeMappingValues,
  compactExposeMappingFields,
  groupExposeFields,
  getExposeFieldOptions,
  isAutoDefaultExposeFieldKey,
  isCalculatedExposeFieldKey,
  isFieldUsedByVariant,
  isPvsolExposeFieldKey,
  normalizeExposeAutomationVariant,
  normalizeExposeMappingFieldsKeepEmpty,
} from '../../lib/exposeAutomation';
import ExposeRawBulkStudio from './ExposeRawBulkStudio';

const CONTRACTS_AM_URL = process.env.REACT_APP_CONTRACTS_AM_URL
  || 'https://gjouokpgdwlcdjdenpdt.supabase.co/functions/v1/contracts-am';

const VISIBILITY_PRESETS_STORAGE_KEY = 'enpower.exposes.visibilityPresets';
const EXPOSE_PREFERENCES_CONTRACT_KEY = 'expose-user-preferences';

// Preset-Namen, die exakt einem Variantenkey entsprechen, erhalten einen freundlichen Anzeigenamen.
const EXPOSE_VARIANT_PRESET_LABELS = {
  'expose-projektverkauf': 'Projektrechte',
  'expose-turnkey': 'Turnkey',
  'expose-wirtschaftlichkeit': 'Wirtschaftlichkeit',
};
function presetDisplayLabel(name) {
  return EXPOSE_VARIANT_PRESET_LABELS[name] || name;
}

// Bringt ein beliebiges Preset-Rohobjekt in die Form { [name]: string[] }.
// Toleriert JSON-Strings (Gesamtobjekt oder einzelne Listen), da die
// Supabase-Persistenz Werte je nach Pfad als String zurückgeben kann.
function normalizeVisibilityPresets(value) {
  let source = value;
  if (typeof source === 'string') {
    try { source = JSON.parse(source); } catch (_) { return {}; }
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) return {};
  const result = {};
  Object.entries(source).forEach(([name, list]) => {
    const key = String(name || '').trim();
    if (!key) return;
    if (Array.isArray(list)) {
      result[key] = list.map((entry) => String(entry)).filter(Boolean);
    } else if (typeof list === 'string') {
      try {
        const parsed = JSON.parse(list);
        if (Array.isArray(parsed)) result[key] = parsed.map((entry) => String(entry)).filter(Boolean);
      } catch (_) { /* ignore malformed entry */ }
    }
  });
  return result;
}

function loadVisibilityPresetsLocal() {
  try {
    const raw = window.localStorage?.getItem(VISIBILITY_PRESETS_STORAGE_KEY);
    return raw ? normalizeVisibilityPresets(raw) : {};
  } catch (_) {
    return {};
  }
}

function persistVisibilityPresetsLocal(presets) {
  try {
    window.localStorage?.setItem(VISIBILITY_PRESETS_STORAGE_KEY, JSON.stringify(presets || {}));
  } catch (_) { /* ignore */ }
}

const inputStyle = {
  width: '100%',
  border: '1px solid #CBD5E1',
  borderRadius: 8,
  padding: '8px 10px',
  color: '#0F172A',
  fontSize: 12,
  background: '#FFFFFF',
};

const buttonBaseStyle = {
  minHeight: 36,
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #CBD5E1',
  background: '#FFFFFF',
  color: '#475569',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

function getExposeStateKey(variantKey) {
  return normalizeExposeAutomationVariant(variantKey);
}

function buildInitialMapping() {
  return Object.fromEntries(EXPOSE_PLACEHOLDER_FIELDS.map((field) => [
    field.placeholder,
    DEFAULT_EXPOSE_BITRIX_MAPPING[field.placeholder] || '',
  ]));
}

function pageLabel(number, title) {
  return (
    <div style={{ margin: '18px 0 8px', color: '#64748B', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
      Seite {number} · {title}
    </div>
  );
}

function ExposePreview({ data, variantKey }) {
  const isTurnkey = variantKey === 'expose-turnkey';
  return (
    <div className="proposal-pages" data-testid="proposal-canvas-expose-automation" style={{ alignItems: 'center' }}>
      {pageLabel(1, isTurnkey ? 'Deckblatt Turnkey' : 'Deckblatt Projektrechte')}
      <ProjectExposeCover data={data} variant={variantKey} editMode={false} showMarkers={false} />

      {pageLabel(2, 'Wirtschaft + Standort + Netzanschluss')}
      <ProjectExposePage2 data={data} variant={variantKey} editMode={false} showMarkers={false} />

      {pageLabel(3, 'PVSol + Prüfungen + Lageplan')}
      <ProjectExposePage3 data={data} variant={variantKey} editMode={false} showMarkers={false} />

      {isTurnkey ? (
        <>
          {pageLabel(4, 'Ertragsprognose + Cashflow + OPEX')}
          <ProjectExposePage4 data={data} variant={variantKey} editMode={false} showMarkers={false} />
        </>
      ) : null}
      <div style={{ height: 32 }} />
    </div>
  );
}

function formatDe(val, decimals = 0) {
  if (!val && val !== 0) return '–';
  return Number(val).toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function ResultRow({ label, value, unit, bold }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0', borderBottom: '1px solid #F1F5F9' }}>
      <span style={{ color: '#475569', fontSize: 12 }}>{label}</span>
      <span style={{ color: '#0F172A', fontSize: 12, fontWeight: bold ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}>
        {typeof value === 'number' ? formatDe(value, Number.isInteger(value) ? 0 : 1) : value}
        {unit ? <span style={{ color: '#94A3B8', fontSize: 11, marginLeft: 3 }}>{unit}</span> : null}
      </span>
    </div>
  );
}

function ResultSection({ title, children }) {
  const filtered = React.Children.toArray(children).filter(Boolean);
  if (!filtered.length) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color: '#122A5C', fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6, paddingBottom: 4, borderBottom: '2px solid #122A5C' }}>
        {title}
      </div>
      {filtered}
    </div>
  );
}

function PvsolImportPanel({ apiBase }) {
  const [input, setInput] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const searchTimer = useRef(null);

  const isNumericId = /^\d+$/.test(input.replace(/^D_/i, '').trim());

  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const response = await axios.get(`${apiBase}/automation/pvsol-webhook`, {
        params: { search: query },
      });
      setSearchResults(response.data?.deals || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suche fehlgeschlagen.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [apiBase]);

  const handleInputChange = useCallback((e) => {
    const val = e.target.value;
    setInput(val);
    setImportResult(null);

    const cleanVal = val.replace(/^D_/i, '').trim();
    if (/^\d+$/.test(cleanVal)) {
      setSearchResults(null);
      return;
    }

    clearTimeout(searchTimer.current);
    if (cleanVal.length >= 2) {
      searchTimer.current = setTimeout(() => handleSearch(cleanVal), 400);
    } else {
      setSearchResults(null);
    }
  }, [handleSearch]);

  const handleSelectDeal = useCallback((deal) => {
    setInput(deal.id);
    setSearchResults(null);
    setImportResult(null);
  }, []);

  const handleImport = useCallback(async (targetDealId) => {
    const id = (targetDealId || input).replace(/^D_/i, '').trim();
    if (!id) { toast.error('Bitte Deal-ID oder Dealname eingeben.'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const response = await axios.get(`${apiBase}/automation/pvsol-webhook`, {
        params: { deal_id: id, force: '1' },
      });
      setImportResult(response.data);
      if (response.data?.ok && !response.data?.skipped) {
        toast.success('PV*SOL Daten erfolgreich importiert!');
      } else if (response.data?.skipped) {
        toast.info(`Übersprungen: ${response.data.reason === 'no_pvsol_file' ? 'Keine PV*SOL-Datei im Deal' : response.data.reason}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Import fehlgeschlagen.';
      toast.error(detail);
      setImportResult({ ok: false, error: detail });
    } finally {
      setImporting(false);
    }
  }, [apiBase, input]);

  const data = importResult?.extracted || {};
  const pvsolData = importResult?._pvsolData;

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '28px 32px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: '#122A5C', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap size={22} color="#F28A0D" />
          </div>
          <div>
            <h2 style={{ margin: 0, color: '#122A5C', fontFamily: 'Outfit, sans-serif', fontSize: 20, fontWeight: 700, letterSpacing: '-0.03em' }}>
              PV*SOL Datenimport
            </h2>
            <p style={{ margin: '2px 0 0', color: '#64748B', fontSize: 12 }}>
              PV*SOL-Datei aus Bitrix-Deal auswerten und Daten in Exposé-DB &amp; Bitrix eintragen
            </p>
          </div>
        </div>

        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <label style={{ display: 'block', color: '#475569', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
            Deal-ID oder Dealname
          </label>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'relative', display: 'flex', gap: 8 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={(e) => { if (e.key === 'Enter' && isNumericId) handleImport(); }}
                  placeholder="z.B. 7022 oder Gardelegen"
                  style={{ ...inputStyle, paddingRight: 34 }}
                />
                {searching ? (
                  <Loader2 size={16} color="#94A3B8" style={{ position: 'absolute', right: 10, top: '50%', marginTop: -8, animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Search size={16} color="#94A3B8" style={{ position: 'absolute', right: 10, top: '50%', marginTop: -8 }} />
                )}
              </div>
              <button
                onClick={() => handleImport()}
                disabled={importing || !input.trim()}
                style={{
                  ...buttonBaseStyle,
                  borderColor: '#122A5C',
                  background: importing ? '#CBD5E1' : '#122A5C',
                  color: '#FFFFFF',
                  opacity: importing || !input.trim() ? 0.65 : 1,
                  cursor: importing || !input.trim() ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {importing ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={14} />}
                {importing ? 'Importiert...' : 'Import starten'}
              </button>
            </div>

            {searchResults && searchResults.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 4, background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto' }}>
                {searchResults.map((deal) => (
                  <button
                    key={deal.id}
                    onClick={() => handleSelectDeal(deal)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', border: 'none', borderBottom: '1px solid #F1F5F9', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontSize: 12 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#F8FAFC'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ color: '#94A3B8', fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 48 }}>#{deal.id}</span>
                    <span style={{ color: '#0F172A', flex: 1 }}>{deal.title}</span>
                    {deal.hasPvsolFile ? (
                      <span style={{ background: '#DCFCE7', color: '#166534', padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>PVSol</span>
                    ) : (
                      <span style={{ background: '#FEF3C7', color: '#92400E', padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>Keine Datei</span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {searchResults && searchResults.length === 0 && !searching && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 4, background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8, padding: '12px 14px', color: '#64748B', fontSize: 12 }}>
                Keine Deals gefunden.
              </div>
            )}
          </div>
          <p style={{ margin: '8px 0 0', color: '#94A3B8', fontSize: 11 }}>
            Deal-ID eingeben (z.B. 7022) und Enter drücken, oder Dealname eingeben um zu suchen.
          </p>
        </div>

        {importResult?.ok && !importResult?.skipped && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', background: '#F0FDF4', borderBottom: '1px solid #BBF7D0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <CheckCircle2 size={20} color="#16A34A" />
              <div>
                <div style={{ color: '#166534', fontSize: 13, fontWeight: 700 }}>Import erfolgreich</div>
                <div style={{ color: '#15803D', fontSize: 11 }}>
                  Deal #{importResult.dealId} · {importResult.filename} · {importResult.exposeFieldsWritten} Exposé-Felder · {importResult.bitrixFieldsWritten} Bitrix-Felder
                </div>
              </div>
            </div>
            <div style={{ padding: 20 }}>
              <ResultSection title="Anlagendaten">
                <ResultRow label="Generatorleistung" value={data.gesamt_kwp} unit="kWp" bold />
                <ResultRow label="Modulanzahl" value={data.gesamt_modulanzahl} bold />
                {data.modul && (
                  <ResultRow label="Modultyp" value={`${data.modul.hersteller} ${data.modul.modell} (${data.modul.leistung_wp} Wp)`.trim()} />
                )}
                {data.wechselrichter_summary && (
                  <ResultRow label="Wechselrichter" value={data.wechselrichter_summary} />
                )}
                <ResultRow label="Generatorfläche" value={data.gesamt_modulflaeche_m2} unit="m²" />
                <ResultRow label="Dachfläche" value={data.gesamt_dachflaeche_m2} unit="m²" />
                <ResultRow label="Belegungsgrad" value={data.belegungsgrad_prozent} unit="%" />
              </ResultSection>
              <ResultSection title="Ertragsprognose">
                <ResultRow label="Jahresertrag" value={data.jahresertrag_kwh ? Math.round(data.jahresertrag_kwh) : null} unit="kWh" bold />
                <ResultRow label="Spez. Jahresertrag" value={data.spez_jahresertrag_kwh_kwp ? Math.round(data.spez_jahresertrag_kwh_kwp * 10) / 10 : null} unit="kWh/kWp" />
                <ResultRow label="Netzeinspeisung" value={data.netzeinspeisung_kwh ? Math.round(data.netzeinspeisung_kwh) : null} unit="kWh" />
                <ResultRow label="Eigenverbrauch" value={data.eigenverbrauch_kwh ? Math.round(data.eigenverbrauch_kwh) : null} unit="kWh" />
                <ResultRow label="Performance Ratio" value={data.performance_ratio ? Math.round(data.performance_ratio * 1000) / 10 : null} unit="%" />
                <ResultRow label="Vermiedene CO₂" value={data.vermiedene_co2_kg ? Math.round(data.vermiedene_co2_kg) : null} unit="kg/Jahr" />
              </ResultSection>
              {(data.stromgestehungskosten_ct || data.investitionen_eur || data.kapitalwert_eur) && (
                <ResultSection title="Wirtschaftlichkeit">
                  <ResultRow label="Investitionen" value={data.investitionen_eur ? formatDe(Math.round(data.investitionen_eur)) : null} unit="€" bold />
                  <ResultRow label="Stromgestehungskosten" value={data.stromgestehungskosten_ct ? Math.round(data.stromgestehungskosten_ct * 100) / 100 : null} unit="ct/kWh" />
                  <ResultRow label="Kapitalwert" value={data.kapitalwert_eur ? formatDe(Math.round(data.kapitalwert_eur)) : null} unit="€" />
                  <ResultRow label="Amortisation" value={data.amortisationszeit_jahre || null} unit="Jahre" />
                  <ResultRow label="Rendite" value={data.rendite_prozent || null} unit="%" />
                  <ResultRow label="Eigenkapitalrendite" value={data.eigenkapitalrendite_prozent || null} unit="%" />
                </ResultSection>
              )}
              {(data.gewichtete_einspeiseverguetung_ct || data.jahresverguetung_eur) && (
                <ResultSection title="Vergütung & Einspeisung">
                  <ResultRow label="EEG-Vergütung (gew.)" value={data.gewichtete_einspeiseverguetung_ct ? Math.round(data.gewichtete_einspeiseverguetung_ct * 100) / 100 : null} unit="ct/kWh" />
                  <ResultRow label="Jahresvergütung" value={data.jahresverguetung_eur ? formatDe(Math.round(data.jahresverguetung_eur)) : null} unit="€" bold />
                  <ResultRow label="Gesamtvergütung" value={data.gesamtverguetung_eur ? formatDe(Math.round(data.gesamtverguetung_eur)) : null} unit="€" />
                  <ResultRow label="Vergütungszeitraum" value={data.verguetungszeitraum_jahre || null} unit="Jahre" />
                  {data.eeg_tarif_name && <ResultRow label="Tarif" value={data.eeg_tarif_name} />}
                  {data.eeg_verguetungsstufen?.length > 0 && data.eeg_verguetungsstufen.map((s, i) => (
                    <ResultRow key={i} label={`  ab ${s.ab_kwp} kWp`} value={Math.round(s.ct_kwh * 100) / 100} unit="ct/kWh" />
                  ))}
                </ResultSection>
              )}
              {data.perioden_ergebnisse?.length > 0 && (
                <ResultSection title="Cashflow-Übersicht">
                  <div style={{ overflowX: 'auto', marginTop: 4 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #122A5C' }}>
                          <th style={{ textAlign: 'left', padding: '4px 6px', color: '#475569', fontWeight: 600 }}>Jahr</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', color: '#475569', fontWeight: 600 }}>Vergütung</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', color: '#475569', fontWeight: 600 }}>Einsparung</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', color: '#475569', fontWeight: 600 }}>Betriebsk.</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', color: '#475569', fontWeight: 600 }}>Kum. CF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.perioden_ergebnisse.map((p, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                            <td style={{ padding: '3px 6px', color: '#0F172A' }}>{p.jahr}</td>
                            <td style={{ padding: '3px 6px', color: '#0F172A', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatDe(Math.round(p.einspeiseverguetung))} €</td>
                            <td style={{ padding: '3px 6px', color: '#0F172A', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatDe(Math.round(p.einsparung))} €</td>
                            <td style={{ padding: '3px 6px', color: '#0F172A', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatDe(Math.round(p.betriebskosten))} €</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: p.cashflow_kumuliert >= 0 ? '#16A34A' : '#DC2626', fontWeight: 600 }}>{formatDe(Math.round(p.cashflow_kumuliert))} €</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </ResultSection>
              )}
              {data.pvsol_version && (
                <div style={{ color: '#94A3B8', fontSize: 11, marginTop: 8 }}>
                  Quelle: {data.pvsol_version}
                </div>
              )}
            </div>
          </div>
        )}

        {importResult?.ok && importResult?.skipped && (
          <div style={{ background: '#FFFFFF', border: '1px solid #FED7AA', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#9A3412' }}>
              <Search size={20} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>Übersprungen</div>
                <div style={{ fontSize: 12, marginTop: 2 }}>
                  {importResult.reason === 'no_pvsol_file' && 'Keine PV*SOL-Datei (.pvprj) im Deal hinterlegt.'}
                  {importResult.reason === 'not_a_pvprj_file' && `Die Datei "${importResult.filename}" ist kein PV*SOL-Archiv.`}
                  {importResult.reason === 'file_not_downloadable' && 'Die Datei konnte nicht heruntergeladen werden.'}
                  {importResult.reason === 'file_unchanged' && 'Die Datei wurde seit dem letzten Import nicht geändert.'}
                  {!['no_pvsol_file', 'not_a_pvprj_file', 'file_not_downloadable', 'file_unchanged'].includes(importResult.reason) && (importResult.reason || 'Unbekannter Grund')}
                </div>
              </div>
            </div>
          </div>
        )}

        {importResult && !importResult.ok && (
          <div style={{ background: '#FFFFFF', border: '1px solid #FECACA', borderRadius: 12, padding: 20 }}>
            <div style={{ color: '#991B1B', fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Fehler</div>
            <div style={{ color: '#7F1D1D', fontSize: 12 }}>{importResult.error || 'Unbekannter Fehler'}</div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default function ExposeAutomationStudio({
  apiBase = '/api',
  session = null,
  toolAccess = {},
  onOpenInTemplateEditor,
}) {
  const accessToken = session?.access_token || null;
  const isAuthenticated = Boolean(accessToken);
  const canEdit = toolAccess?.canEdit === true;
  const canCreate = toolAccess?.canCreate === true || canEdit;
  const authHeaders = useMemo(() => (
    accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
  ), [accessToken]);

  const [activeVariantKey, setActiveVariantKey] = useState(EXPOSE_AUTOMATION_VARIANTS[0].key);
  const [filterByVariant, setFilterByVariant] = useState(() => {
    try { return window.localStorage?.getItem('enpower.exposes.filterByVariant') === 'true'; } catch (_) { return false; }
  });
  const [studioMode, setStudioMode] = useState('bulk');
  const [dealId, setDealId] = useState('');
  const [mappingDraft, setMappingDraft] = useState(buildInitialMapping);
  const [defaultsDraft, setDefaultsDraft] = useState({});
  const defaultsLoadedRef = useRef(false);
  const [mappingSavedAt, setMappingSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewSummary, setPreviewSummary] = useState(null);
  const [unresolved, setUnresolved] = useState([]);

  // Sichtbarkeits-Presets
  const [visibilityPresets, setVisibilityPresets] = useState(() => loadVisibilityPresetsLocal());
  const [selectedPresetForEdit, setSelectedPresetForEdit] = useState('');
  const [presetDraft, setPresetDraft] = useState(null);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const presetsLoadedRef = useRef(false);

  const stateKey = getExposeStateKey(activeVariantKey);
  const activeVariant = EXPOSE_AUTOMATION_VARIANTS.find((variant) => variant.key === activeVariantKey)
    || EXPOSE_AUTOMATION_VARIANTS[0];
  const groupedFields = useMemo(() => groupExposeFields(EXPOSE_PLACEHOLDER_FIELDS), []);
  const filteredGroupedFields = useMemo(() => {
    if (!filterByVariant) return groupedFields;
    const result = {};
    for (const [group, fields] of Object.entries(groupedFields)) {
      const filtered = fields.filter((f) => isFieldUsedByVariant(f, activeVariantKey));
      if (filtered.length > 0) result[group] = filtered;
    }
    return result;
  }, [groupedFields, filterByVariant, activeVariantKey]);
  const totalFieldCount = EXPOSE_PLACEHOLDER_FIELDS.length;
  const visibleFieldCount = Object.values(filteredGroupedFields).reduce((sum, arr) => sum + arr.length, 0);
  const placeholderData = useMemo(() => buildExposePlaceholderData(activeVariantKey), [activeVariantKey]);
  const effectivePreviewData = previewData || placeholderData;
  const staleFieldKeys = useMemo(() => {
    const known = new Set(EXPOSE_PLACEHOLDER_FIELDS.map((field) => field.placeholder));
    return Object.keys(mappingDraft || {})
      .filter((key) => !known.has(key))
      .sort((a, b) => a.localeCompare(b, 'de'));
  }, [mappingDraft]);

  const loadMapping = useCallback(async () => {
    if (!isAuthenticated) {
      setMappingDraft(buildInitialMapping());
      setMappingSavedAt(null);
      return;
    }

    try {
      const response = await axios.get(CONTRACTS_AM_URL, {
        params: { op: 'mapping', contract_key: stateKey },
        headers: authHeaders,
      });
      const mapping = response.data?.mapping;
      // Keep-Empty: gespeicherte Leerstrings überschreiben den Default aus buildInitialMapping,
      // damit vom Nutzer entfernte Mappings entfernt BLEIBEN. Danach wertbasierter Legacy-Cleanup
      // (nur bekannte Alt-/Müllwerte werden geleert – bewusst neu gesetzte Mappings überleben).
      const merged = cleanupLegacyExposeMappingValues({
        ...buildInitialMapping(),
        ...normalizeExposeMappingFieldsKeepEmpty(mapping?.fields || {}),
      });
      setMappingDraft(merged);
      setMappingSavedAt(mapping?.updatedAt || null);
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      if (detail) toast.error(`Exposé-Mapping nicht ladbar: ${detail}`);
      setMappingDraft(buildInitialMapping());
      setMappingSavedAt(null);
    }
  }, [authHeaders, isAuthenticated, stateKey]);

  // Standardwerte gelten global für alle Varianten -> variant-unabhängig, nur einmal laden.
  const loadDefaults = useCallback(async () => {
    if (!isAuthenticated) {
      setDefaultsDraft({});
      defaultsLoadedRef.current = false;
      return;
    }
    if (defaultsLoadedRef.current) return;
    defaultsLoadedRef.current = true;

    try {
      const response = await axios.get(CONTRACTS_AM_URL, {
        params: { op: 'mapping', contract_key: EXPOSE_FIELD_DEFAULTS_CONTRACT_KEY },
        headers: authHeaders,
      });
      const mapping = response.data?.mapping;
      setDefaultsDraft(compactExposeMappingFields(mapping?.fields || {}));
    } catch (error) {
      console.error(error);
      defaultsLoadedRef.current = false;
    }
  }, [authHeaders, isAuthenticated]);

  useEffect(() => {
    loadMapping();
    loadDefaults();
    setPreviewData(null);
    setPreviewSummary(null);
    setUnresolved([]);
  }, [loadMapping, loadDefaults]);

  // Sichtbarkeits-Presets aus Supabase laden (einmalig beim Mount).
  useEffect(() => {
    if (!isAuthenticated || presetsLoadedRef.current) return;
    presetsLoadedRef.current = true;
    let cancelled = false;
    axios.get(CONTRACTS_AM_URL, {
      params: { op: 'mapping', contract_key: EXPOSE_PREFERENCES_CONTRACT_KEY },
      headers: authHeaders,
    }).then((response) => {
      if (cancelled) return;
      const prefs = response.data?.mapping?.fields;
      if (prefs && typeof prefs === 'object') {
        if (prefs.visibilityPresets != null) {
          const normalized = normalizeVisibilityPresets(prefs.visibilityPresets);
          setVisibilityPresets(normalized);
          persistVisibilityPresetsLocal(normalized);
        }
        if (typeof prefs.filterByVariant === 'boolean') {
          setFilterByVariant(prefs.filterByVariant);
          try { window.localStorage?.setItem('enpower.exposes.filterByVariant', String(prefs.filterByVariant)); } catch (_) { /* ignore */ }
        }
      }
    }).catch(() => { /* use localStorage fallback */ });
    return () => { cancelled = true; };
  }, [authHeaders, isAuthenticated]);

  const buildPreferencesPayload = useCallback((overrides = {}) => {
    let showExtendedFields = false;
    try {
      const raw = window.localStorage?.getItem('enpower.exposes.showExtendedFields');
      showExtendedFields = raw === 'true' || raw === '1';
    } catch (_) { /* ignore */ }
    return {
      showExtendedFields,
      filterByVariant,
      visibilityPresets: normalizeVisibilityPresets(visibilityPresets),
      ...overrides,
    };
  }, [filterByVariant, visibilityPresets]);

  const persistPreferencesToSupabase = useCallback((payload) => {
    if (!authHeaders?.Authorization) return;
    axios.post(CONTRACTS_AM_URL, {
      op: 'mapping',
      contract_key: EXPOSE_PREFERENCES_CONTRACT_KEY,
      fields: payload,
    }, { headers: authHeaders }).catch(() => { /* best-effort */ });
  }, [authHeaders]);

  // Persist presets to both localStorage and Supabase.
  const persistPresetsEverywhere = useCallback((nextPresets) => {
    const normalized = normalizeVisibilityPresets(nextPresets);
    setVisibilityPresets(normalized);
    persistVisibilityPresetsLocal(normalized);
    persistPreferencesToSupabase(buildPreferencesPayload({ visibilityPresets: normalized }));
  }, [buildPreferencesPayload, persistPreferencesToSupabase]);

  const handleSelectPresetForEdit = useCallback((name) => {
    setSelectedPresetForEdit(name);
    if (name && visibilityPresets[name]) {
      setPresetDraft([...visibilityPresets[name]]);
    } else {
      setPresetDraft(null);
    }
  }, [visibilityPresets]);

  const handleNewPreset = useCallback(() => {
    const name = window.prompt('Name für neues Preset:');
    if (name == null) return;
    const trimmed = String(name).trim();
    if (!trimmed) {
      toast.error('Name darf nicht leer sein.');
      return;
    }
    setSelectedPresetForEdit(trimmed);
    setPresetDraft([]);
    setPresetsOpen(true);
  }, []);

  const handleSavePreset = useCallback(() => {
    if (!selectedPresetForEdit) {
      toast.error('Kein Preset ausgewählt.');
      return;
    }
    if (!Array.isArray(presetDraft)) {
      toast.error('Kein Preset-Entwurf vorhanden.');
      return;
    }
    const nextPresets = { ...visibilityPresets, [selectedPresetForEdit]: [...presetDraft] };
    persistPresetsEverywhere(nextPresets);
    toast.success(`Preset "${presetDisplayLabel(selectedPresetForEdit)}" gespeichert.`);
  }, [selectedPresetForEdit, presetDraft, visibilityPresets, persistPresetsEverywhere]);

  const handleDeletePreset = useCallback(() => {
    if (!selectedPresetForEdit) return;
    const label = presetDisplayLabel(selectedPresetForEdit);
    if (!window.confirm(`Preset "${label}" wirklich löschen?`)) return;
    const nextPresets = { ...visibilityPresets };
    delete nextPresets[selectedPresetForEdit];
    persistPresetsEverywhere(nextPresets);
    setSelectedPresetForEdit('');
    setPresetDraft(null);
    toast.success(`Preset "${label}" gelöscht.`);
  }, [selectedPresetForEdit, visibilityPresets, persistPresetsEverywhere]);

  const handleTogglePresetField = useCallback((dataKey) => {
    setPresetDraft((current) => {
      if (!Array.isArray(current)) return current;
      if (current.includes(dataKey)) {
        return current.filter((k) => k !== dataKey);
      }
      return [...current, dataKey];
    });
  }, []);

  const handleMappingChange = useCallback((placeholder, value) => {
    setMappingDraft((current) => ({ ...current, [placeholder]: value }));
  }, []);

  const handleDefaultChange = useCallback((placeholder, value) => {
    setDefaultsDraft((current) => ({ ...current, [placeholder]: value }));
  }, []);

  const handleRemoveStaleField = useCallback((placeholder) => {
    setMappingDraft((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, placeholder)) return current;
      const next = { ...current };
      delete next[placeholder];
      return next;
    });
  }, []);

  const handleSaveMapping = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    if (!canEdit) {
      toast.error('Keine Berechtigung zum Bearbeiten des Exposé-Mappings.');
      return;
    }

    setSaving(true);
    try {
      await axios.post(CONTRACTS_AM_URL, {
        op: 'mapping',
        contract_key: stateKey,
        fields: mappingDraft,
      }, { headers: authHeaders });

      // Standardwerte global (variant-unabhängig) speichern, nur nicht-leere Werte.
      await axios.post(CONTRACTS_AM_URL, {
        op: 'mapping',
        contract_key: EXPOSE_FIELD_DEFAULTS_CONTRACT_KEY,
        fields: compactExposeMappingFields(defaultsDraft),
      }, { headers: authHeaders });

      await axios.post(CONTRACTS_AM_URL, {
        op: 'mapping',
        contract_key: EXPOSE_PREFERENCES_CONTRACT_KEY,
        fields: buildPreferencesPayload(),
      }, { headers: authHeaders });

      setMappingSavedAt(new Date().toISOString());
      toast.success('Mapping, Standardwerte & Presets gespeichert.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }, [authHeaders, buildPreferencesPayload, canEdit, defaultsDraft, isAuthenticated, mappingDraft, stateKey]);

  const handleRunPreview = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    if (!canCreate) {
      toast.error('Keine Berechtigung zum Erstellen einer Exposé-Vorschau.');
      return;
    }
    if (!dealId.trim()) {
      toast.error('Bitte eine Bitrix-Deal-ID eingeben.');
      return;
    }

    setPreviewing(true);
    try {
      const response = await axios.post(`${apiBase}/contracts/usage`, {
        action: 'expose-preview',
        expose_key: stateKey,
        deal_id: dealId.trim(),
        mapping: { fields: mappingDraft },
        fields: EXPOSE_PLACEHOLDER_FIELDS,
      }, { headers: authHeaders });

      const data = response.data?.proposalData && typeof response.data.proposalData === 'object'
        ? response.data.proposalData
        : buildExposeDataFromResolvedFields(response.data?.resolvedFields || {}, stateKey, { includePlaceholders: true });
      setPreviewData(data);
      setPreviewSummary(response.data?.bitrixSummary || null);
      setUnresolved(Array.isArray(response.data?.unresolved) ? response.data.unresolved : []);
      toast.success('Exposé-Vorschau gefüllt.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Exposé-Vorschau fehlgeschlagen.');
    } finally {
      setPreviewing(false);
    }
  }, [apiBase, authHeaders, canCreate, dealId, isAuthenticated, mappingDraft, stateKey]);

  const handleResetPreview = useCallback(() => {
    setPreviewData(null);
    setPreviewSummary(null);
    setUnresolved([]);
  }, []);

  const modeBar = (
    <div style={{ borderBottom: '1px solid #D8E1EC', background: '#FFFFFF', padding: '10px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <FileText size={18} color="#122A5C" />
        <div>
          <div style={{ color: '#122A5C', fontSize: 14, fontWeight: 900, fontFamily: 'Outfit, sans-serif' }}>Exposés erstellen</div>
          <div style={{ color: '#64748B', fontSize: 11 }}>EN:POWER Projektrechte & Turnkey</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => setStudioMode('bulk')}
          style={{
            ...buttonBaseStyle,
            minHeight: 32,
            borderColor: studioMode === 'bulk' ? '#F28A0D' : '#CBD5E1',
            background: studioMode === 'bulk' ? '#F28A0D' : '#FFFFFF',
            color: studioMode === 'bulk' ? '#FFFFFF' : '#0F172A',
          }}
        >
          Rohdateien / Bulk
        </button>
        <button
          type="button"
          onClick={() => setStudioMode('mapping')}
          style={{
            ...buttonBaseStyle,
            minHeight: 32,
            borderColor: studioMode === 'mapping' ? '#F28A0D' : '#CBD5E1',
            background: studioMode === 'mapping' ? '#F28A0D' : '#FFFFFF',
            color: studioMode === 'mapping' ? '#FFFFFF' : '#0F172A',
          }}
        >
          Mapping
        </button>
        <button
          type="button"
          onClick={() => setStudioMode('pvsol')}
          style={{
            ...buttonBaseStyle,
            minHeight: 32,
            borderColor: studioMode === 'pvsol' ? '#F28A0D' : '#CBD5E1',
            background: studioMode === 'pvsol' ? '#F28A0D' : '#FFFFFF',
            color: studioMode === 'pvsol' ? '#FFFFFF' : '#0F172A',
          }}
        >
          <Zap size={14} />
          PV*SOL Import
        </button>
      </div>
    </div>
  );

  if (studioMode === 'pvsol') {
    return (
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#F8FAFC' }}>
        {modeBar}
        <PvsolImportPanel apiBase={apiBase} />
      </div>
    );
  }

  if (studioMode === 'bulk') {
    return (
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#F8FAFC' }}>
        {modeBar}
        <ExposeRawBulkStudio
          activeVariantKey={activeVariantKey}
          onVariantChange={setActiveVariantKey}
          apiBase={apiBase}
          authHeaders={authHeaders}
          bitrixMapping={mappingDraft}
          onOpenInTemplateEditor={onOpenInTemplateEditor}
        />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#F8FAFC' }}>
      {modeBar}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '420px minmax(0, 1fr)', minHeight: 0, background: '#F8FAFC' }}>
      <aside style={{ minHeight: 0, overflowY: 'auto', borderRight: '1px solid #E2E8F0', background: '#FFFFFF' }}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid #E2E8F0', display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileText size={20} color="#122A5C" />
            <div>
              <h2 style={{ margin: 0, color: '#122A5C', fontFamily: 'Outfit, sans-serif', fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em' }}>
                Exposés - Mapping
              </h2>
              <p style={{ margin: '4px 0 0', color: '#64748B', fontSize: 12, lineHeight: 1.45 }}>
                Platzhalter-Felder und ihre Quellen: Bitrix, PV*SOL, Auto-Berechnung oder manuell.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            {EXPOSE_AUTOMATION_VARIANTS.map((variant) => {
              const active = variant.key === activeVariantKey;
              return (
                <button
                  key={variant.key}
                  onClick={() => setActiveVariantKey(variant.key)}
                  style={{
                    ...buttonBaseStyle,
                    borderColor: active ? '#F28A0D' : '#CBD5E1',
                    background: active ? '#F28A0D' : '#FFFFFF',
                    color: active ? '#FFFFFF' : '#0F172A',
                  }}
                >
                  {variant.label}
                </button>
              );
            })}
          </div>

          <label style={{ display: 'grid', gap: 5, color: '#475569', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Bitrix Deal-ID
            <input
              value={dealId}
              onChange={(event) => setDealId(event.target.value)}
              placeholder="z.B. D_7086 oder 7086"
              style={inputStyle}
            />
          </label>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={handleRunPreview}
              disabled={previewing || !isAuthenticated || !dealId.trim() || !canCreate}
              style={{
                ...buttonBaseStyle,
                borderColor: '#122A5C',
                background: previewing ? '#CBD5E1' : '#122A5C',
                color: '#FFFFFF',
                opacity: previewing || !isAuthenticated || !dealId.trim() || !canCreate ? 0.65 : 1,
                cursor: previewing || !isAuthenticated || !dealId.trim() || !canCreate ? 'not-allowed' : 'pointer',
              }}
            >
              <Play size={14} />
              {previewing ? 'Füllt...' : 'Vorschau füllen'}
            </button>
            <button
              onClick={handleSaveMapping}
              disabled={saving || !isAuthenticated || !canEdit}
              style={{
                ...buttonBaseStyle,
                opacity: saving || !isAuthenticated || !canEdit ? 0.65 : 1,
                cursor: saving || !isAuthenticated || !canEdit ? 'not-allowed' : 'pointer',
              }}
            >
              <Save size={14} />
              {saving ? 'Speichert...' : 'Mapping speichern'}
            </button>
            <button onClick={handleResetPreview} style={buttonBaseStyle}>
              <RotateCcw size={14} />
              Platzhalter
            </button>
          </div>

          {previewSummary ? (
            <div style={{ border: '1px solid #BBF7D0', borderRadius: 8, background: '#F0FDF4', color: '#166534', padding: '9px 10px', fontSize: 12, lineHeight: 1.45, display: 'flex', gap: 8 }}>
              <CheckCircle2 size={16} />
              <span>
                Deal #{previewSummary.dealId || dealId} geladen{previewSummary.dealTitle ? ` · ${previewSummary.dealTitle}` : ''}.
              </span>
            </div>
          ) : null}

          {unresolved.length > 0 ? (
            <div style={{ border: '1px solid #FED7AA', borderRadius: 8, background: '#FFF7ED', color: '#9A3412', padding: '9px 10px', fontSize: 12, lineHeight: 1.45 }}>
              Nicht befüllt: {unresolved.slice(0, 8).map((item) => `{{${item}}}`).join(', ')}
              {unresolved.length > 8 ? ` +${unresolved.length - 8}` : ''}
            </div>
          ) : null}
        </div>

        <div style={{ padding: '10px 0 14px' }}>
          <div style={{ padding: '0 16px 8px', color: '#64748B', fontSize: 12, lineHeight: 1.45 }}>
            Mapping-Ausdrücke funktionieren wie bei Verträgen, z.B. <code>DEAL.TITLE</code>, <code>COMPANY.TITLE</code>, <code>CONTACT.EMAIL</code> oder mehrere Felder getrennt mit Komma/Semikolon.
          </div>
          <div style={{ padding: '0 16px 8px', color: '#64748B', fontSize: 12, lineHeight: 1.45 }}>
            Priorität beim Laden: Bitrix-Mapping → PV*SOL-Datei → Standardwert. Felder mit <strong>Berechnet</strong>-Tag werden automatisch aus anderen Werten errechnet und überschreiben ggf. Mapping/Standardwert. Standardwerte können Formeln sein: <code>=4 * {'{Leistung kWp}'}</code> berechnet z.B. 4 × kWp.
          </div>

          <div style={{ padding: '0 16px 10px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: filterByVariant ? '#1D4ED8' : '#64748B', fontWeight: filterByVariant ? 700 : 400 }}>
              <input
                type="checkbox"
                checked={filterByVariant}
                onChange={(e) => {
                  const next = e.target.checked;
                  setFilterByVariant(next);
                  try { window.localStorage?.setItem('enpower.exposes.filterByVariant', String(next)); } catch (_) { /* ignore */ }
                  persistPreferencesToSupabase(buildPreferencesPayload({ filterByVariant: next }));
                }}
                style={{ accentColor: '#1D4ED8' }}
              />
              Nur Vorlagen-Felder ({activeVariant.label})
            </label>
            <span style={{ fontSize: 11, color: '#94A3B8' }}>
              {visibleFieldCount} von {totalFieldCount} Feldern
            </span>
          </div>

          {Object.entries(filteredGroupedFields).map(([group, fields]) => (
            <div key={group} style={{ borderTop: '1px solid #E2E8F0' }}>
              <div style={{ padding: '9px 16px 5px', color: '#122A5C', fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {group}
              </div>
              {fields.map((field) => {
                const isPvsol = (field.dataKeys || []).some((key) => isPvsolExposeFieldKey(key));
                const isCalc = (field.dataKeys || []).some((key) => isCalculatedExposeFieldKey(key));
                const isAuto = (field.dataKeys || []).some((key) => isAutoDefaultExposeFieldKey(key));
                const isMapped = Boolean(String(mappingDraft[field.placeholder] || '').trim());
                const isManual = !isMapped && !isPvsol && !isCalc && !isAuto;
                return (
                  <div key={field.placeholder} style={{ padding: '6px 16px', display: 'grid', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <label style={{ color: '#0F172A', fontSize: 12, fontWeight: 700, fontFamily: 'monospace' }}>
                        {`{{${field.placeholder}}}`}
                      </label>
                      {isMapped ? (
                        <span
                          title="Wird aus einem Bitrix-Feld befüllt"
                          style={{ background: '#DBEAFE', color: '#1D4ED8', borderRadius: 4, fontSize: 10, fontWeight: 700, padding: '1px 6px' }}
                        >
                          Bitrix
                        </span>
                      ) : null}
                      {isPvsol ? (
                        <span
                          title="Wird automatisch aus der PV*SOL-Datei befüllt"
                          style={{ background: '#FEF3C7', color: '#92400E', borderRadius: 4, fontSize: 10, fontWeight: 700, padding: '1px 6px' }}
                        >
                          PV*SOL
                        </span>
                      ) : null}
                      {isCalc ? (
                        <span
                          title="Wird automatisch berechnet — Bitrix-Mapping/Standardwert wird überschrieben"
                          style={{ background: '#F0FDF4', color: '#166534', borderRadius: 4, fontSize: 10, fontWeight: 700, padding: '1px 6px' }}
                        >
                          Berechnet
                        </span>
                      ) : null}
                      {isAuto ? (
                        <span
                          title="Wird automatisch mit einem Default-Wert befüllt"
                          style={{ background: '#ECFDF5', color: '#065F46', borderRadius: 4, fontSize: 10, fontWeight: 700, padding: '1px 6px' }}
                        >
                          Auto
                        </span>
                      ) : null}
                      {isManual ? (
                        <span
                          title="Nur manuelle Eingabe — kein automatischer Wert"
                          style={{ background: '#EDE9FE', color: '#6D28D9', borderRadius: 4, fontSize: 10, fontWeight: 700, padding: '1px 6px' }}
                        >
                          Manuell
                        </span>
                      ) : null}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <div style={{ display: 'grid', gap: 3 }}>
                        <span style={{ fontSize: 10, color: '#94A3B8' }}>Quelle (Bitrix-Feld / leer = manuell)</span>
                        <input
                          value={mappingDraft[field.placeholder] || ''}
                          onChange={(event) => handleMappingChange(field.placeholder, event.target.value)}
                          placeholder="z.B. DEAL.TITLE"
                          style={inputStyle}
                        />
                      </div>
                      <div style={{ display: 'grid', gap: 3 }}>
                        <span style={{ fontSize: 10, color: '#94A3B8' }}>Standardwert</span>
                        {(() => {
                          const opts = getExposeFieldOptions(field.placeholder);
                          const currentVal = defaultsDraft[field.placeholder] || '';
                          if (opts) {
                            return (
                              <select
                                value={opts.includes(currentVal) ? currentVal : ''}
                                onChange={(event) => handleDefaultChange(field.placeholder, event.target.value)}
                                style={inputStyle}
                              >
                                <option value="">— kein Standard —</option>
                                {opts.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                                {currentVal && !opts.includes(currentVal) ? (
                                  <option value={currentVal}>{currentVal}</option>
                                ) : null}
                              </select>
                            );
                          }
                          return (
                            <input
                              value={currentVal}
                              onChange={(event) => handleDefaultChange(field.placeholder, event.target.value)}
                              placeholder="Wert oder =Formel"
                              title="Fester Wert oder Formel mit = (z.B. =4 * {Leistung kWp})"
                              style={{
                                ...inputStyle,
                                ...(currentVal.startsWith('=') ? { background: '#F0F9FF', borderColor: '#93C5FD' } : {}),
                              }}
                            />
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Sichtbarkeits-Presets */}
          <div style={{ borderTop: '1px solid #E2E8F0' }}>
            <button
              type="button"
              onClick={() => setPresetsOpen((v) => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                padding: '9px 16px 5px', color: '#122A5C', fontSize: 11, fontWeight: 900,
                textTransform: 'uppercase', letterSpacing: '0.08em', background: 'none',
                border: 'none', cursor: 'pointer', textAlign: 'left',
              }}
            >
              {presetsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              Sichtbarkeits-Presets
              <span style={{ fontSize: 10, fontWeight: 500, color: '#94A3B8', textTransform: 'none', letterSpacing: 0 }}>
                ({Object.keys(visibilityPresets).length})
              </span>
            </button>

            {presetsOpen && (
              <div style={{ padding: '6px 16px 14px' }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                  <select
                    value={selectedPresetForEdit}
                    onChange={(e) => handleSelectPresetForEdit(e.target.value)}
                    style={{ ...inputStyle, width: 170, flex: 'none', fontSize: 11 }}
                  >
                    <option value="">-- Preset wählen --</option>
                    {Object.keys(visibilityPresets)
                      .sort((a, b) => presetDisplayLabel(a).localeCompare(presetDisplayLabel(b), 'de'))
                      .map((name) => (
                        <option key={name} value={name}>{presetDisplayLabel(name)}</option>
                      ))}
                  </select>
                  <button type="button" onClick={handleNewPreset} style={{ ...buttonBaseStyle, minHeight: 30, padding: '5px 10px', fontSize: 11 }}>
                    Neues Preset
                  </button>
                  <button
                    type="button"
                    onClick={handleDeletePreset}
                    disabled={!selectedPresetForEdit}
                    title="Ausgewähltes Preset löschen"
                    style={{ ...buttonBaseStyle, minHeight: 30, padding: '5px 8px', opacity: selectedPresetForEdit ? 1 : 0.45 }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>

                {presetDraft != null && (
                  <div>
                    <div style={{ color: '#64748B', fontSize: 11, marginBottom: 8 }}>
                      Angekreuzt = sichtbar, nicht angekreuzt = ausgeblendet im Exposé.
                    </div>
                    {Object.entries(groupedFields).map(([group, fields]) => (
                      <div key={group} style={{ marginBottom: 6 }}>
                        <div style={{ color: '#122A5C', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '4px 0 2px' }}>
                          {group}
                        </div>
                        {fields.map((field) => {
                          const dataKey = (field.dataKeys || [])[0];
                          if (!dataKey) return null;
                          const isHidden = presetDraft.includes(dataKey);
                          return (
                            <label key={dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 12, color: '#0F172A', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={!isHidden}
                                onChange={() => handleTogglePresetField(dataKey)}
                                style={{ margin: 0 }}
                              />
                              <span>{field.placeholder}</span>
                              <span style={{ color: '#94A3B8', fontSize: 10 }}>({dataKey})</span>
                            </label>
                          );
                        })}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleSavePreset}
                      style={{
                        ...buttonBaseStyle, marginTop: 8,
                        borderColor: '#122A5C', background: '#122A5C', color: '#FFFFFF',
                      }}
                    >
                      <Save size={14} />
                      Preset speichern
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {staleFieldKeys.length > 0 ? (
            <div style={{ margin: '10px 16px 0', border: '1px solid #FECACA', borderRadius: 8, background: '#FEF2F2', overflow: 'hidden' }}>
              <div style={{ padding: '8px 10px', color: '#991B1B', fontSize: 12, fontWeight: 700 }}>Alte Mapping-Felder</div>
              {staleFieldKeys.map((key) => (
                <div key={key} style={{ padding: '7px 10px', borderTop: '1px solid #FECACA', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <code style={{ flex: 1, color: '#7F1D1D', fontSize: 11, overflowWrap: 'anywhere' }}>{key}</code>
                  <button onClick={() => handleRemoveStaleField(key)} style={{ ...buttonBaseStyle, minHeight: 28, padding: '4px 8px', fontSize: 11 }}>
                    Entfernen
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          <div style={{ padding: '10px 16px 0', color: '#64748B', fontSize: 11 }}>
            {mappingSavedAt ? `Gespeichert: ${new Date(mappingSavedAt).toLocaleString('de-DE')}` : 'Noch kein Mapping gespeichert.'}
          </div>
        </div>
      </aside>

      <main style={{ minHeight: 0, overflow: 'auto', padding: '22px 28px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <p style={{ margin: 0, color: '#64748B', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {previewData ? 'Bitrix-Vorschau' : 'Platzhalter-Vorlage'}
            </p>
            <h3 style={{ margin: '3px 0 0', color: '#122A5C', fontFamily: 'Outfit, sans-serif', fontSize: 20, fontWeight: 700, letterSpacing: '-0.03em' }}>
              {activeVariant.label}
            </h3>
          </div>
          <span style={{ border: '1px solid #E2E8F0', borderRadius: 999, background: '#FFFFFF', color: '#64748B', padding: '6px 10px', fontSize: 11, fontWeight: 700 }}>
            {EXPOSE_PLACEHOLDER_FIELDS.length} Platzhalter
          </span>
        </div>

        <div className="proposal-staging" data-proposal-canvas="true" style={{ background: 'transparent', padding: 0 }}>
          <ExposePreview data={effectivePreviewData} variantKey={activeVariantKey} />
        </div>
      </main>
      </div>
    </div>
  );
}
